import React, { useEffect, useRef, useState } from "react";
import { Formik, Form } from "formik";
import { describeBusinessValidationSchema } from "../../utils/ValidationSchema";
import { DescribeBusinessFormType } from "../../utils/formType";
import { DescribeBusinessInitialFormValues } from "../../utils/initialFormValues";
import DescribeBusinessComponent from "./components/DescribeBusinessComponent";
import { BusinessDescriptionType } from "types";
import {
  useUpdateBusinessDescriptionMutation,
  useUpdateBusinessWebsiteMutation,
} from "types/generated/operations";
import { getProspectUuid } from "utils/idHelpers";
import { convertStringIntoBool, errorMessageMap } from "utils/helper";
import FooterButton from "uicomponents/FooterButton";
import Alert from "uicomponents/Alert";
import {
  BACK_BUTTON_TEXT,
  NEXT_BUTTON_TEXT,
  LOADING_BUTTON_TEXT,
} from "pages/WorkersCompApplcation/utils/contants";
import {
  trackFormError,
  trackPageViewUserStep,
} from "utils/analytics/trackEvent";

interface IDescribeBusinessProps {
  setNextStep: () => void;
  setPreviousStep: () => void;
  data: BusinessDescriptionType;
}

const DescribeBusiness = ({
  ...props
}: IDescribeBusinessProps): JSX.Element => {
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageViewUserStep(7, "describe business");
  }, []);

  const { setNextStep, setPreviousStep, data } = props;

  const initialFormValues: DescribeBusinessFormType =
    DescribeBusinessInitialFormValues(data);

  const [
    updateBusinessDescription,
    { loading: updateBusinessDescriptionLoading },
  ] = useUpdateBusinessDescriptionMutation();
  const [updateBusinessWebsite, { loading: updateBusinessWebsiteLoading }] =
    useUpdateBusinessWebsiteMutation();
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoadingSteps, setIsLoadingSteps] = useState(false);

  const handleOnSubmit = async (values: DescribeBusinessFormType) => {
    setIsLoadingSteps(true);
    try {
      await updateBusinessDescription({
        variables: {
          uuid: getProspectUuid(),
          businessDescription: values.businessDescription,
          dailyOperationsOfEmployees: values.dailyOperationsOfEmployees,
          hasUnionWorkers: convertStringIntoBool(values.hasUnionWorkers),
        },
      });
      await updateBusinessWebsite({
        variables: {
          uuid: getProspectUuid(),
          website: values.website.filter((url) => !!url.trim()),
          companyName: data?.companyName,
          dba: data?.dba,
          ein: data?.ein,
          yearFounded: data?.yearFounded,
        },
      });
      setNextStep();
    } catch (error: any) {
      const errorMessage: string = error?.message;
      errorMessageMap[errorMessage] &&
        setErrorMessage(errorMessageMap[errorMessage]);
      trackFormError(error, errorMessage);
      setIsError(true);
      return;
    }
  };

  const handleBack = (): void => {
    setPreviousStep();
  };

  const fieldRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  // Helper function that makes the browser scroll a specific field into view
  const focusField = (fieldName: string) => {
    if (fieldRefs.current[fieldName]) {
      fieldRefs.current[fieldName]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  return (
    <>
      <Formik
        initialValues={initialFormValues}
        validationSchema={describeBusinessValidationSchema}
        onSubmit={handleOnSubmit}
      >
        {({ errors, handleSubmit, values }) => {
          return (
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                const firstErrorFieldName = Object.keys(errors)[0];
                // Handle website validation errors
                if (
                  firstErrorFieldName === "website" &&
                  errors.website &&
                  errors.website.length > 0
                ) {
                  focusField("website[0]");
                } else {
                  focusField(firstErrorFieldName);
                }
                handleSubmit(e);
              }}
            >
              <DescribeBusinessComponent
                data={props.data}
                formValues={values}
                fieldRefs={fieldRefs}
              />
              <FooterButton
                nextButtonText={
                  updateBusinessDescriptionLoading ||
                  updateBusinessWebsiteLoading ||
                  isLoadingSteps
                    ? LOADING_BUTTON_TEXT
                    : NEXT_BUTTON_TEXT
                }
                backButtonText={BACK_BUTTON_TEXT}
                disabled={
                  updateBusinessDescriptionLoading ||
                  updateBusinessWebsiteLoading ||
                  isLoadingSteps
                }
                handleBackClick={handleBack}
              />
              {isError && <Alert message={errorMessage} />}
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default DescribeBusiness;
